import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMatches } from 'react-router-dom';
import type { RouteHandle } from './GlobalToolbar';

/**
 * Полноэкранные на телефоне экраны. Канон рисует часть рабочих мест (сессия ОТК,
 * портал цеха) на vw < 768 БЕЗ рейки сайдбара и тулбара — весь экран отдан работе,
 * возврат идёт крошкой самой страницы. Признак объявляет либо маршрут
 * (`handle.mobileChromeless`), либо сам экран хуком `useMobileChromeless()` —
 * никаких списков путей в шелле.
 */
interface ShellChromeApi {
  /** Экран занял весь телефон: рейка сайдбара и тулбар скрыты (< 768). */
  mobileChromeless: boolean;
  claimMobileChromeless: () => () => void;
}

const ShellChromeCtx = createContext<ShellChromeApi | null>(null);

export function ShellChromeProvider({ children }: { children: ReactNode }) {
  // Счётчик, а не флаг: StrictMode прогоняет эффекты дважды, и пара claim/release
  // остаётся сбалансированной при любом порядке размонтирования экранов.
  const [claims, setClaims] = useState(0);
  const matches = useMatches();
  const byRoute = matches.some((m) => (m.handle as RouteHandle | undefined)?.mobileChromeless === true);

  const claimMobileChromeless = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => Math.max(0, n - 1));
  }, []);

  const value = useMemo<ShellChromeApi>(
    () => ({ mobileChromeless: byRoute || claims > 0, claimMobileChromeless }),
    [byRoute, claims, claimMobileChromeless],
  );

  return <ShellChromeCtx.Provider value={value}>{children}</ShellChromeCtx.Provider>;
}

export function useShellChrome(): ShellChromeApi {
  const ctx = useContext(ShellChromeCtx);
  if (!ctx) throw new Error('useShellChrome() вызван вне <ShellChromeProvider>');
  return ctx;
}

/**
 * Экран объявляет себя полноэкранным на телефоне (< 768). Вне шелла (юнит-тест
 * экрана в изоляции) хук молчит — экран рендерится как обычно.
 */
export function useMobileChromeless(enabled = true): void {
  const ctx = useContext(ShellChromeCtx);
  const claim = ctx?.claimMobileChromeless;
  useEffect(() => {
    if (!enabled || !claim) return;
    return claim();
  }, [enabled, claim]);
}
